/**
 * CourseLevelPage — /agentic-interviewing/level/:levelNumber
 *
 * Displays one course level with dual-audience toggle (Candidate / Hiring Manager).
 * Handles all lock/access states.
 *
 * States handled:
 *   - Not logged in (Level 1): full read, no Practice buttons
 *   - Not logged in (Level 2–4): blurred preview + sign-up prompt
 *   - Free account (Level 1): full access including practice
 *   - Free account (Level 2–4): content preview + upgrade prompt
 *   - All Access (Level 2–4 locked by progression): lock message
 *   - All Access (unlocked): full access
 *   - Completed level: badge display + next-level CTA
 *
 * AUDIENCE TOGGLE
 *   Local state only — defaults to "candidate". If URL-driven persistence is
 *   needed in future, migrate to useSearchParams(?view=hiring-manager).
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { COURSE_LEVEL_CONTENT } from "../data/courseLevelContent";
import { L } from "../utils/localized";
import { PRICING, fmtUSD } from "../constants/pricing";
import { getPhase2QuestionsForLevel, groupByDomain } from "../data/phase2Questions";
import { getPhase3ProbesForLevel, getProbesForDomain } from "../data/phase3Probes";
import { completeCourseLevel, getCourseProgress, getLevelQuestions } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";
import QuestionDeepDive from "../components/QuestionDeepDive";
import FollowUpProbeCard from "../components/FollowUpProbeCard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Audience = "candidate" | "hiringManager";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function QuestionCard({
  question,
  canPractice,
  practiced,
}: {
  question: { id: string; question_text: string; competency_tags: string[]; difficulty: string };
  canPractice: boolean;
  practiced: boolean;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");

  const handlePractice = () => {
    navigate("/evaluate", { state: { questionId: question.id } });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${practiced ? alpha(theme.palette.secondary.main, 0.4) : theme.palette.divider}`,
        borderRadius: 2,
        p: 2.5,
        transition: "border-color 0.2s",
        bgcolor: practiced ? alpha(theme.palette.secondary.main, 0.03) : "background.paper",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box flex={1}>
          <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.55, mb: 1 }}>
            {question.question_text}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {question.competency_tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag.replace(/_/g, " ")}
                size="small"
                sx={{
                  fontSize: "0.68rem",
                  height: 20,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "text.secondary",
                }}
              />
            ))}
            <Chip
              label={question.difficulty}
              size="small"
              sx={{ fontSize: "0.68rem", height: 20, bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}
            />
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={1} flexShrink={0}>
          {practiced && (
            <CheckCircleIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
          )}
          {canPractice && (
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={handlePractice}
              sx={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}
            >
              {practiced ? t("CourseLevelPage.practiceAgainButton") : t("CourseLevelPage.submitForEvaluationButton")}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function StickyProgressBar({
  practicesCount,
  completed,
  onComplete,
  completing,
  levelColor,
}: {
  practicesCount: number;
  completed: boolean;
  onComplete: () => void;
  completing: boolean;
  levelColor: string;
}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const required = 3;
  const pct = Math.min((practicesCount / required) * 100, 100);
  const canComplete = practicesCount >= required && !completed;

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 100,
        bgcolor: "background.paper",
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
        py: 1.5,
        px: 3,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        spacing={2}
        maxWidth="lg"
        mx="auto"
      >
        {/* progress ticks */}
        <Stack direction="row" spacing={1} alignItems="center">
          {[1, 2, 3].map((n) => (
            <Box
              key={n}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: `2px solid ${n <= practicesCount ? levelColor : theme.palette.divider}`,
                bgcolor: n <= practicesCount ? levelColor : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s",
              }}
            >
              {n <= practicesCount && (
                <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} />
              )}
            </Box>
          ))}
          <Typography variant="body2" color="text.secondary" ml={1}>
            {completed
              ? t("CourseLevelPage.levelCompleteMessage")
              : t("CourseLevelPage.practicesCompleteCount", { count: Math.min(practicesCount, 3) })}
          </Typography>
        </Stack>

        {/* progress bar */}
        <Box flex={1} sx={{ display: { xs: "none", sm: "block" } }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(levelColor, 0.15),
              "& .MuiLinearProgress-bar": { bgcolor: levelColor, borderRadius: 4 },
            }}
          />
        </Box>

        {/* complete button */}
        {!completed && (
          <Button
            variant="contained"
            size="small"
            disabled={!canComplete || completing}
            onClick={onComplete}
            startIcon={
              completing ? <CircularProgress size={14} color="inherit" /> : <EmojiEventsIcon />
            }
            sx={{
              bgcolor: canComplete ? levelColor : undefined,
              "&:hover": { bgcolor: canComplete ? alpha(levelColor, 0.85) : undefined },
              fontWeight: 500,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {completing ? t("CourseLevelPage.completingButtonBusy") : t("CourseLevelPage.completeLevelButton")}
          </Button>
        )}
        {completed && (
          <Chip
            icon={<EmojiEventsIcon />}
            label={t("CourseLevelPage.badgeEarnedLabel")}
            sx={{ bgcolor: alpha(levelColor, 0.12), color: levelColor, fontWeight: 500 }}
          />
        )}
      </Stack>
    </Box>
  );
}

// LEVEL_COLORS is defined inside the component (see below) so it can reference theme tokens.

// band is an i18n key, resolved via t() at render (pages-core.json CourseLevelPage.seniorityBand*)
const SENIORITY_LABELS: Record<number, { bandKey: string; range: string }> = {
  1: { bandKey: "CourseLevelPage.seniorityBandIntermediate", range: "$130K–$220K" },
  2: { bandKey: "CourseLevelPage.seniorityBandSenior", range: "$220K–$400K" },
  3: { bandKey: "CourseLevelPage.seniorityBandStaff", range: "$350K–$600K" },
  4: { bandKey: "CourseLevelPage.seniorityBandPrincipal", range: "$500K–$850K+" },
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CourseLevelPage() {
  const { levelNumber } = useParams<{ levelNumber: string }>();
  const level = parseInt(levelNumber ?? "1", 10);
  const theme = useTheme();
  const { t, i18n } = useTranslation("pages-core");

  // Level color palette (matches CourseHome) — defined here to reference theme tokens.
  const LEVEL_COLORS: Record<number, string> = {
    1: theme.palette.success.main,
    2: theme.palette.info.main,
    3: theme.palette.primary.main,
    4: theme.palette.warning.main,
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [audience, setAudience] = useState<Audience>("candidate");

  const levelColor = LEVEL_COLORS[level] ?? LEVEL_COLORS[1];
  const seniority = SENIORITY_LABELS[level];
  const courseLevelContent = L(COURSE_LEVEL_CONTENT, i18n.language);
  const content = courseLevelContent[level];

  // Access rules
  const isLoggedIn = !!user;
  const tierAccess = getTierAccess(user);
  const canAccessLevel = level === 1 || tierAccess.canAccessProducts;
  const canPractice = isLoggedIn && canAccessLevel;

  // Course progress
  const { data: progressData } = useQuery({
    queryKey: ["courseProgress"],
    queryFn: getCourseProgress,
    enabled: isLoggedIn,
  });
  const levelProgress = progressData?.levels?.find(
    (l: { level: number }) => l.level === level
  );
  const completed = levelProgress?.completed ?? false;
  const locked = levelProgress?.locked ?? level > 1;
  const practicesCount = levelProgress?.practices_count ?? 0;

  // Questions for this level
  const { data: questions = [], isLoading: questionsLoading } = useQuery({
    queryKey: ["levelQuestions", level],
    queryFn: () => getLevelQuestions(level),
    enabled: canAccessLevel,
  });

  // Complete level mutation
  const completeMutation = useMutation({
    mutationFn: () => completeCourseLevel(level),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courseProgress"] });
    },
  });

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5">{t("CourseLevelPage.levelNotFoundMessage")}</Typography>
        <Button onClick={() => navigate("/agentic-interviewing")} sx={{ mt: 2 }}>
          {t("CourseLevelPage.backToCourseButton")}
        </Button>
      </Container>
    );
  }

  const audienceContent = audience === "candidate" ? content.candidate : content.hiringManager;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>

      {/* ── Level header ─────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(levelColor, 0.12)} 0%, ${alpha(levelColor, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(levelColor, 0.2)}`,
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          {/* breadcrumb */}
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/agentic-interviewing")}
              size="small"
              sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
            >
              {t("CourseLevelPage.courseBreadcrumb")}
            </Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("CourseLevelPage.levelBreadcrumb", { level })}</Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
                <Chip
                  label={t("CourseLevelPage.levelChipLabel", { level })}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    letterSpacing: "0.07em",
                    bgcolor: alpha(levelColor, 0.12),
                    color: levelColor,
                  }}
                />
                {seniority && (
                  <Chip
                    label={`${t(seniority.bandKey)} · ${seniority.range}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      borderColor: alpha(levelColor, 0.3),
                      color: "text.secondary",
                    }}
                  />
                )}
              </Stack>
              <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 0.5 }}>
                {content.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {content.subtitle}
              </Typography>
            </Box>
            {completed && (
              <Chip
                icon={<EmojiEventsIcon />}
                label={t("CourseLevelPage.badgeEarnedLabel")}
                sx={{
                  bgcolor: alpha(levelColor, 0.12),
                  color: levelColor,
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  py: 0.5,
                }}
              />
            )}
          </Stack>

          {/* practice progress bar */}
          {isLoggedIn && canAccessLevel && !completed && (
            <Box mt={3}>
              <Stack direction="row" justifyContent="space-between" mb={0.75}>
                <Typography variant="caption" color="text.secondary">
                  {t("CourseLevelPage.practiceProgressLabel")}
                </Typography>
                <Typography variant="caption" fontWeight={500} sx={{ color: levelColor }}>
                  {t("CourseLevelPage.practicesRequiredCount", { count: Math.min(practicesCount, 3) })}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min((practicesCount / 3) * 100, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(levelColor, 0.15),
                  "& .MuiLinearProgress-bar": { bgcolor: levelColor, borderRadius: 4 },
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

        {/* ── Access gate (Level 2–4 non-subscriber) ─────── */}
        {!canAccessLevel && (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Alert
              severity="info"
              icon={<LockIcon />}
              sx={{ mb: 4, borderRadius: 2 }}
              action={
                <Button
                  size="small"
                  onClick={() => navigate("/pricing")}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t("CourseLevelPage.getAllAccessButton")}
                </Button>
              }
            >
              {isLoggedIn
                ? t("CourseLevelPage.allAccessRequiredMessage", { price: fmtUSD(PRICING.allAccess.monthly) })
                : t("CourseLevelPage.createFreeAccountMessage")}
            </Alert>
          )
        )}

        {/* ── Progression gate (All Access but level locked) */}
        {canAccessLevel && locked && (
          <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 4, borderRadius: 2 }}>
            {t("CourseLevelPage.progressionGateMessage", { previousLevel: level - 1 })}
          </Alert>
        )}

        {/* ── Audience note ──────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${alpha(levelColor, 0.3)}`,
            bgcolor: alpha(levelColor, 0.04),
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
            💡 {content.audienceNote}
          </Typography>
        </Paper>

        {/* ── Audience toggle ────────────────────────────── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          sx={{
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t("CourseLevelPage.viewingAsLabel")}
          </Typography>
          <ToggleButtonGroup
            value={audience}
            exclusive
            onChange={(_, v) => v && setAudience(v as Audience)}
            size="small"
          >
            <ToggleButton
              value="candidate"
              sx={{
                px: 2,
                fontSize: "0.8rem",
                fontWeight: 500,
                gap: 0.75,
                "&.Mui-selected": {
                  bgcolor: alpha(levelColor, 0.12),
                  color: levelColor,
                  borderColor: alpha(levelColor, 0.3),
                },
              }}
            >
              <PersonIcon sx={{ fontSize: 16 }} />
              {t("CourseLevelPage.candidateToggle")}
            </ToggleButton>
            <ToggleButton
              value="hiringManager"
              sx={{
                px: 2,
                fontSize: "0.8rem",
                fontWeight: 500,
                gap: 0.75,
                "&.Mui-selected": {
                  bgcolor: alpha(levelColor, 0.12),
                  color: levelColor,
                  borderColor: alpha(levelColor, 0.3),
                },
              }}
            >
              <BusinessCenterIcon sx={{ fontSize: 16 }} />
              {t("CourseLevelPage.hiringManagerToggle")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* ── Introduction prose ─────────────────────────── */}
        <Box mb={5}>
          {audienceContent.intro.map((para, i) => (
            <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: "text.primary" }}>
              {para}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ── Domain matrix ──────────────────────────────── */}
        <Box mb={5}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={2}>
            <Typography variant="h6" fontWeight={500}>
              {t("CourseLevelPage.competencyDomainsHeading", {
                band: seniority ? t(seniority.bandKey) : "",
              })}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {audience === "candidate"
                ? t("CourseLevelPage.whatYouMustDemonstrate")
                : t("CourseLevelPage.whatToExpectFromCandidates")}
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {content.domainMatrix.map((domain) => (
              <Paper
                key={domain.code}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                  transition: "border-color 0.2s",
                  "&:hover": { borderColor: alpha(levelColor, 0.4) },
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                  {/* Domain badge */}
                  <Box flexShrink={0}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: alpha(levelColor, 0.12),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        sx={{ color: levelColor, letterSpacing: "0.04em", fontSize: "0.75rem" }}
                      >
                        {domain.code}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Domain content */}
                  <Box flex={1} minWidth={0}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                      mb={0.75}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {domain.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: "italic",
                          color: "text.disabled",
                          flexShrink: 0,
                          display: { xs: "none", md: "block" },
                        }}
                      >
                        "{domain.marketSignal}"
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.5 }}>
                      {domain.levelExpectation}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
                      {t("CourseLevelPage.scopeLabel", { scope: domain.scopeDescriptor })}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ── Candidate: Key concepts / HM: What to look for */}
        <Box mb={5}>
          <Typography variant="h6" fontWeight={500} gutterBottom>
            {audience === "candidate"
              ? t("CourseLevelPage.keyConceptsHeading")
              : t("CourseLevelPage.whatToLookForHeading")}
          </Typography>
          <Stack spacing={1} mt={2}>
            {(audience === "candidate"
              ? content.candidate.keyConcepts
              : content.hiringManager.whatToLookFor
            ).map((item) => (
              <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: levelColor,
                    mt: "9px",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* ── Candidate: Outcomes / HM: Red flags + calibration */}
        {audience === "candidate" ? (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${alpha(levelColor, 0.25)}`,
              borderRadius: 3,
              p: 3,
              mb: 5,
              bgcolor: alpha(levelColor, 0.03),
            }}
          >
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              {t("CourseLevelPage.whatYouCanDoHeading")}
            </Typography>
            <Stack spacing={1.5} mt={1.5}>
              {content.candidate.whatYouCanDo.map((item) => (
                <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleIcon
                    sx={{ color: levelColor, fontSize: 18, mt: "2px", flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={3} mb={5}>
            {/* Red flags */}
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                borderRadius: 3,
                p: 3,
                bgcolor: alpha(theme.palette.error.main, 0.02),
              }}
            >
              <Typography variant="subtitle1" fontWeight={500} gutterBottom sx={{ color: theme.palette.error.dark }}>
                {t("CourseLevelPage.redFlagsHeading")}
              </Typography>
              <Stack spacing={1.5} mt={1.5}>
                {content.hiringManager.redFlags.map((flag) => (
                  <Stack key={flag} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: theme.palette.error.main,
                        mt: "9px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                      {flag}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            {/* Calibration notes */}
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                borderRadius: 3,
                p: 3,
                bgcolor: alpha(theme.palette.warning.main, 0.02),
              }}
            >
              <Typography variant="subtitle1" fontWeight={500} gutterBottom sx={{ color: theme.palette.warning.main }}>
                {t("CourseLevelPage.calibrationNotesHeading")}
              </Typography>
              <Stack spacing={1.5} mt={1.5}>
                {content.hiringManager.calibrationNotes.map((note) => (
                  <Stack key={note} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: theme.palette.warning.main,
                        mt: "9px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                      {note}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Stack>
        )}

        {/* ── Scoring guide (Level 1 only, shared across audiences) ─ */}
        {content.scoringGuide && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box mb={5}>
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("CourseLevelPage.scoringRubricHeading")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {audience === "candidate"
                  ? t("CourseLevelPage.scoringRubricDescriptionCandidate")
                  : t("CourseLevelPage.scoringRubricDescriptionHiringManager")}
              </Typography>
              <Stack spacing={1.5}>
                {content.scoringGuide.map((band) => {
                  // AMBIGUOUS: bg values ([hex], [hex], [hex], [hex], [hex]) are
                  // tinted status backgrounds with no direct MUI theme.palette equivalent.
                  // Mapped to theme.palette.background.default per task instructions but this
                  // loses the per-score color differentiation. A dedicated palette extension
                  // (e.g. theme.palette.error.lighter) would be the correct fix.
                  const bandColors: Record<number, { bg: string; border: string; label: string }> = {
                    1: { bg: theme.palette.background.default, border: theme.palette.error.light, label: theme.palette.error.dark },
                    2: { bg: theme.palette.background.default, border: theme.palette.warning.main, label: theme.palette.warning.main },
                    3: { bg: theme.palette.background.default, border: theme.palette.warning.main, label: theme.palette.primary.main }, // AMBIGUOUS: [hex] coral → primary.main
                    4: { bg: theme.palette.background.default, border: theme.palette.success.light, label: theme.palette.success.dark },
                    5: { bg: theme.palette.background.default, border: theme.palette.info.light, label: theme.palette.info.dark },
                  };
                  const colors = bandColors[band.score];
                  return (
                    <Paper
                      key={band.score}
                      elevation={0}
                      sx={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: 2,
                        p: 2,
                        bgcolor: colors.bg, // was: colors.bg + "80" (hex opacity concat, now removed — bg is a token)
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: colors.border,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: "white", fontSize: "0.85rem" }}
                          >
                            {band.score}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ color: colors.label, mb: 0.25 }}
                          >
                            {band.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {band.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}

        {/* ── Interview process (Level 1 only, shared across audiences) ─ */}
        {content.interviewProcess && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box mb={5}>
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("CourseLevelPage.interviewStructureHeading")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {audience === "candidate"
                  ? t("CourseLevelPage.interviewStructureDescriptionCandidate")
                  : t("CourseLevelPage.interviewStructureDescriptionHiringManager")}
              </Typography>
              <Stack spacing={1.5}>
                {content.interviewProcess.map((round) => (
                  <Paper
                    key={round.round}
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: 2,
                      "&:hover": { borderColor: alpha(levelColor, 0.4) },
                      transition: "border-color 0.2s",
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: alpha(levelColor, 0.12),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: levelColor, fontSize: "0.8rem" }}
                        >
                          R{round.round}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {round.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ flexShrink: 0 }}
                          >
                            {round.duration}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.55, mb: 0.75 }}
                        >
                          {round.focus}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          {round.domains.map((d) => (
                            <Chip
                              key={d}
                              label={d}
                              size="small"
                              sx={{
                                fontSize: "0.68rem",
                                height: 20,
                                bgcolor: alpha(levelColor, 0.1),
                                color: levelColor,
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* ── Level 4: Scenario links ─────────────────────── */}
        {content.scenarioSlugs && content.scenarioSlugs.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box mb={5}>
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("CourseLevelPage.systemDesignScenariosHeading")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {audience === "candidate"
                  ? t("CourseLevelPage.scenariosDescriptionCandidate")
                  : t("CourseLevelPage.scenariosDescriptionHiringManager")}
              </Typography>
              <Stack spacing={1.5}>
                {content.scenarioSlugs.map((slug) => {
                  const scenarioLabels: Record<string, string> = {
                    "customer-support-automation": t("CourseLevelPage.scenarioCustomerSupportAutomation"),
                    "hedge-fund-research-agent": t("CourseLevelPage.scenarioHedgeFundResearchAgent"),
                    "multi-agent-software-dev-pipeline": t("CourseLevelPage.scenarioMultiAgentSoftwareDevPipeline"),
                    "healthcare-prior-authorization": t("CourseLevelPage.scenarioHealthcarePriorAuthorization"),
                    "ecommerce-returns-processing": t("CourseLevelPage.scenarioEcommerceReturnsProcessing"),
                    "cross-org-supply-chain-coordination": t("CourseLevelPage.scenarioCrossOrgSupplyChainCoordination"),
                  };
                  return (
                    <Paper
                      key={slug}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: alpha(levelColor, 0.5),
                          bgcolor: alpha(levelColor, 0.02),
                        },
                      }}
                      onClick={() =>
                        navigate(`/agentic-interviewing/scenario/${slug}`)
                      }
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {scenarioLabels[slug] ?? slug}
                        </Typography>
                        <ArrowForwardIcon
                          sx={{ fontSize: 18, color: "text.disabled" }}
                        />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}

        {/* ── Phase 2 primaries + Phase 3 probes (Levels 2–4) ─────────── */}
        {(() => {
          const phase2Qs = getPhase2QuestionsForLevel(level as 2 | 3 | 4, i18n.language);
          const phase3Probes = getPhase3ProbesForLevel(level as 2 | 3 | 4, i18n.language);
          if (phase2Qs.length === 0 && phase3Probes.length === 0) return null;
          const grouped = groupByDomain(phase2Qs);
          // If we have probes but no primaries (unlikely), fall back to probe domains
          const domainCodes = Array.from(grouped.keys()).length > 0
            ? Array.from(grouped.keys())
            : Array.from(new Set(phase3Probes.map((p) => p.domainCode)));

          return (
            <>
              <Divider sx={{ my: 4 }} />
              <Box mb={5}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
                  <Typography variant="h6" fontWeight={500}>
                    {t("CourseLevelPage.primaryQuestionsProbesHeading")}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t("CourseLevelPage.primaryQuestionsCount", { count: phase2Qs.length })}
                    {phase3Probes.length > 0 && t("CourseLevelPage.followUpProbesCountSuffix", { count: phase3Probes.length })}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {audience === "candidate"
                    ? t("CourseLevelPage.primaryQuestionsDescriptionCandidate")
                    : t("CourseLevelPage.primaryQuestionsDescriptionHiringManager")}
                </Typography>
                {domainCodes.map((domainCode) => {
                  const domainQs = grouped.get(domainCode) ?? [];
                  const domainProbes = getProbesForDomain(level as 2 | 3 | 4, domainCode, i18n.language);
                  const domainName = domainQs[0]?.domainName ?? domainProbes[0]?.domainName ?? domainCode;

                  return (
                    <Box key={domainCode} mb={5}>
                      {/* Domain header */}
                      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: alpha(levelColor, 0.12),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: levelColor, letterSpacing: "0.04em", fontSize: "0.75rem" }}
                          >
                            {domainCode}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {domainName}
                        </Typography>
                        <Stack direction="row" spacing={0.75}>
                          {domainQs.length > 0 && (
                            <Chip
                              label={t("CourseLevelPage.domainPrimaryCountChip", { count: domainQs.length })}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.68rem", color: "text.disabled", height: 20 }}
                            />
                          )}
                          {domainProbes.length > 0 && (
                            <Chip
                              label={t("CourseLevelPage.domainProbeCountChip", { count: domainProbes.length })}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.68rem", color: "text.disabled", height: 20 }}
                            />
                          )}
                        </Stack>
                      </Stack>

                      {/* Primary questions */}
                      {domainQs.length > 0 && (
                        <Stack spacing={2.5} mb={domainProbes.length > 0 ? 3 : 0}>
                          {domainQs.map((q) => (
                            <QuestionDeepDive
                              key={q.slug}
                              question={q}
                              audience={audience}
                              levelColor={levelColor}
                              canSubmit={canPractice && !locked}
                            />
                          ))}
                        </Stack>
                      )}

                      {/* Follow-up probes for this domain */}
                      {domainProbes.length > 0 && (
                        <Box
                          sx={{
                            pl: { xs: 0, md: 2 },
                            ml: { xs: 0, md: 3 },
                            borderLeft: { xs: "none", md: `1px dashed ${alpha(levelColor, 0.3)}` },
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              color: "text.secondary",
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              mb: 1.5,
                              display: "block",
                            }}
                          >
                            {audience === "candidate"
                              ? t("CourseLevelPage.probesInstructionCandidate")
                              : t("CourseLevelPage.probesInstructionHiringManager")}
                          </Typography>
                          <Stack spacing={2}>
                            {domainProbes.map((probe) => (
                              <FollowUpProbeCard
                                key={probe.slug}
                                probe={probe}
                                audience={audience}
                                levelColor={levelColor}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          );
        })()}

        <Divider sx={{ my: 4 }} />

        {/* ── Practice questions (DB-sourced — primarily Level 1) ──────── */}
        <Box>
          <Typography variant="h6" fontWeight={500} gutterBottom>
            {level === 1 ? t("CourseLevelPage.practiceQuestionsHeading") : t("CourseLevelPage.additionalPracticeHeading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {canPractice ? (
              <Trans
                i18nKey="CourseLevelPage.practiceInstructionsCanPractice"
                t={t}
                components={[<strong key="s0" />]}
              />
            ) : level === 1 ? (
              t("CourseLevelPage.practiceInstructionsSignIn")
            ) : (
              t("CourseLevelPage.practiceInstructionsAllAccess")
            )}
            {audience === "hiringManager" && t("CourseLevelPage.hiringManagerQuestionBankNote")}
          </Typography>

          {questionsLoading ? (
            <Stack spacing={2}>
              {[1, 2, 3].map((n) => (
                <Skeleton key={n} variant="rounded" height={90} />
              ))}
            </Stack>
          ) : questions.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t("CourseLevelPage.questionsCuratedMessage")}
            </Alert>
          ) : (
            <Stack spacing={2}>
              {questions.map(
                (q: {
                  id: string;
                  question_text: string;
                  competency_tags: string[];
                  difficulty: string;
                }) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    canPractice={canPractice && !locked}
                    practiced={levelProgress?.practiced_question_ids?.includes(q.id) ?? false}
                  />
                )
              )}
            </Stack>
          )}
        </Box>

        {/* ── Upgrade prompt (level 2–4 non-subscriber) ─── */}
        {!canAccessLevel && (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 4,
                textAlign: "center",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(levelColor, 0.04)} 100%)`,
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("CourseLevelPage.unlockLevelsHeading")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
              >
                {t("CourseLevelPage.unlockLevelsBody", { price: fmtUSD(PRICING.allAccess.monthly) })}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/pricing")}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontWeight: 500 }}
              >
                {t("CourseLevelPage.getAllAccessPriceButton", { price: fmtUSD(PRICING.allAccess.monthly) })}
              </Button>
            </Paper>
          </Fade>
        )}

        {/* ── Next level CTA (after completion) ─────────── */}
        {completed && level < 4 && (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 4,
                textAlign: "center",
                border: `1px solid ${alpha(LEVEL_COLORS[level + 1], 0.3)}`,
                borderRadius: 3,
                bgcolor: alpha(LEVEL_COLORS[level + 1], 0.03),
              }}
            >
              <EmojiEventsIcon
                sx={{ fontSize: 40, color: LEVEL_COLORS[level + 1], mb: 2 }}
              />
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("CourseLevelPage.levelUnlockedMessage", { level: level + 1 })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {courseLevelContent[level + 1]?.subtitle}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/agentic-interviewing/level/${level + 1}`)}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: LEVEL_COLORS[level + 1],
                  "&:hover": { bgcolor: alpha(LEVEL_COLORS[level + 1], 0.85) },
                  fontWeight: 500,
                }}
              >
                {t("CourseLevelPage.continueToLevelButton", { level: level + 1 })}
              </Button>
            </Paper>
          </Fade>
        )}
      </Container>

      {/* ── Sticky progress bar (logged in + can access) ── */}
      {isLoggedIn && canAccessLevel && !locked && (
        <StickyProgressBar
          practicesCount={practicesCount}
          completed={completed}
          onComplete={() => completeMutation.mutate()}
          completing={completeMutation.isPending}
          levelColor={levelColor}
        />
      )}
    </Box>
  );
}
